/* eslint-disable import/prefer-default-export */
import IOH from './IOH';
import Chart from '@/classes/Chart';

const primaryColor = window.IOC?.colors?.company?.main?.primary;
let resolutionOverride = '';
const timeRangeItems = [
  {
    label: 'Morgen',
    value: 'tomorrow',
  },
  {
    label: 'Heute',
    value: 'today',
  },
  {
    label: 'Aktueller Monat',
    value: 'currentMonth',
  },
  {
    label: 'Letzter Monat',
    value: 'lastMonth',
  },
  {
    label: 'Benutzerdefiniert',
    value: 'custom',
  },
];
const OPTIONS = {
  colors: [IOH.convertColorTo(primaryColor, 'hex')],
  chart: {
    type: 'column',
    toolbar: {
      show: true,
      timeRange: {
        show: true,
        type: 'range', // range or select
        items: timeRangeItems,
        shadowItems: [
          ...timeRangeItems,
          {
            label: 'Tag',
            value: 'day',
          },
          {
            label: 'Monat',
            value: 'month',
          },
        ],
        value: [],
        limits: {
          min: null, // set min limit of the time range
          max: null, // set max limit of the time range
        },
      },
      resolution: {
        show: true,
        value: 'year',
        list: [
          { value: 'hour', label: 'Stunden' },
          { value: 'day', label: 'Tage' },
          { value: 'week', label: 'Wochen' },
          { value: 'month', label: 'Monate' },
          { value: 'year', label: 'Jahre' },
        ],
        filter: (resolutions, timerange, dateDiffs) => {
          if (dateDiffs.years >= 2) {
            return resolutions?.filter((res) => ['hour', 'day', 'week', 'month', 'year'].includes(res.value));
          }
          if (dateDiffs.months >= 2) {
            return resolutions?.filter((res) => ['hour', 'day', 'week', 'month'].includes(res.value));
          }
          if (dateDiffs.weeks >= 2) {
            return resolutions?.filter((res) => ['hour', 'day', 'week'].includes(res.value));
          }
          if (dateDiffs.days >= 2) {
            return resolutions?.filter((res) => ['hour', 'day'].includes(res.value));
          }
          if (dateDiffs.days <= 1) {
            return resolutions?.filter((res) => ['hour'].includes(res.value));
          }

          return resolutions;
        },
      },
    },
    boost: {
      useGPUTranslations: true,
    },
    zooming: {
      type: 'x',
    },
    zoomType: 'x',
    zoomKey: 'ctrl',
    panning: true,
    panKey: 'shift',
  },
  tooltip: {
    enabled: true,
    shared: true,
    intersect: false,
    useHTML: true,
    followCursor: true,
    formatter(tooltip) {
      return tooltip.defaultFormatter.call(this, tooltip);
    },
    style: {
      fontSize: '12px',
    },
  },
  credits: {
    enabled: false,
  },
  time: {
    useUTC: false,
  },
  title: {
    text: undefined,
  },
  legend: {
    enabled: false,
    interactive: true, // enable hiding of series on legend item click
  },
  noData: {
    text: 'Keine Daten verfügbar',
    style: {
      fontSize: '16px',
      fontWeight: 'normal',
      color: '#666666',
    },
    position: {
      align: 'center',
      verticalAlign: 'middle',
    },
  },
  xAxis: {
    events: {
      afterSetExtremes(event) {
        const { chart } = this;
        const xAxis = chart.xAxis[0];

        const range = event.max - event.min;

        if (range < 24 * 3600 * 1000) { // Less than 1 day
          resolutionOverride = 'hour';
        } else {
          resolutionOverride = chart.options.chart.toolbar.resolution.value;
        }

        if (event.trigger !== 'zoom' || event.userMax === undefined || event.userMin === undefined) {
          xAxis.update({
            startOnTick: false,
            minPadding: 0.05,
            maxPadding: 0.05,
            labels: {
              padding: 16,
              step: 1,
            },
            formatter() {
              return Chart.getDateForResolution(
                this.value,
                chart.options.chart.toolbar.resolution.value,
                {
                  separator: '<br>',
                  label: this,
                  formatOverride: this.chart.options.chart.toolbar.resolution.format,
                },
              );
            },
          });
          return;
        }

        xAxis.update({
          startOnTick: true,
          minPadding: 0.15,
          maxPadding: 0.15,
          labels: {
            align: 'center',
            x: 0,
            padding: 16,
            step: 1,
            formatter(label) {
              return Chart.getDateForResolution(
                label.value,
                resolutionOverride,
                {
                  separator: '<br>',
                  label,
                  formatOverride: label.chart.options.chart.toolbar.resolution.format,
                },
              );
            },
          },
        });
      },
    },
    type: 'datetime',
    className: 'x-axis-label',
    startOnTick: false,
    minPadding: 0.05,
    maxPadding: 0.05,
    labels: {
      align: 'center',
      x: 0,
      allowOverlap: false,
      style: {
        textAlign: 'center',
      },
      padding: 16,
      step: 1,
      useHTML: false,
      formatter(label) {
        return Chart.getDateForResolution(
          label.value,
          label.chart.options.chart.toolbar.resolution.value,
          {
            separator: '<br>',
            label,
            formatOverride: label.chart.options.chart.toolbar.resolution.format,
          },
        );
      },
    },
  },
  yAxis: {
    allowDecimals: false,
    title: {
      text: '',
    },
  },
  plotOptions: {
    line: {
      events: {
        legendItemClick(e) {
          const { chart } = this;
          return Chart.canHideData(e, chart.options.legend.interactive);
        },
      },
      showInLegend: true,
    },
    area: {
      events: {
        legendItemClick(e) {
          const { chart } = this;
          return Chart.canHideData(e, chart.options.legend.interactive);
        },
      },
    },
    column: {
      events: {
        legendItemClick(e) {
          const { chart } = this;
          return Chart.canHideData(e, chart.options.legend.interactive);
        },
      },
    },
  },
  // Dummy Data if no series or endpoint defined
  series: [
    {
      name: 'dummy-series-1',
      color: '#FF0000',
      data: Chart.generateData('year'),
    },
    {
      name: 'dummy-series-2',
      color: '#00FF00',
      data: Chart.generateData('year'),
    },
  ],
};

export {
  OPTIONS,
};
